/**
 * Admin → Diagnostics — v3.15-1036
 *
 * Single consolidated admin surface for ALL non-operational integrity /
 * reconciliation / migration / audit tooling. The main operational dashboard
 * surfaces ONLY operational signals (Total Funded SSOT, Pipeline, Pending
 * Funding, Hard-to-Reach, Tasks). Everything diagnostic lives HERE.
 *
 * Funding truth used by every operational path in the CRM:
 *     all_parties_settled == true
 *
 * Anything you see on this page either (a) audits drift from that SSOT or
 * (b) helps admins migrate legacy data. NONE of it affects funded totals,
 * payroll visibility, settlement calculations, or workflows.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, Shield, ArrowRight, Activity, Mail, Wrench, Database } from 'lucide-react';
import {
  ReconcileAllChip,
  SSOTIntegrityChip,
  BookingSlugIssuesWarning,
  RecentEmailSendsSection,
} from './DashboardPage';

const isInternalRole = (role) => (role || '').startsWith('ptc_');
const isAdminRole = (role) => ['ptc_admin', 'super_admin', 'company_director'].includes(role || '');

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

// ─── Unverified Funded Deals table (was on dashboard, now diagnostic-only) ───
const UnverifiedFundedDealsSection = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/dashboard/stats');
        if (alive) setStats(res.data);
      } catch {
        if (alive) setStats({});
      }
    })();
  }, []);
  const count = stats?.unverified_funded_count || 0;
  const volume = stats?.unverified_funded_volume || 0;
  const deals = stats?.unverified_funded_deals || [];

  return (
    <Card className="border border-amber-200" data-testid="diag-unverified-funded">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Unverified Funded Deals
          </CardTitle>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
              {count} deal{count !== 1 ? 's' : ''}
            </Badge>
            <span className="text-sm font-semibold text-amber-700">{formatCurrency(volume)}</span>
          </div>
        </div>
        <p className="text-xs text-amber-700 mt-1">
          Deals with payment evidence but incomplete structure. <strong>NOT</strong> included in operational totals.
          Use this surface to promote them once verified.
        </p>
      </CardHeader>
      <CardContent>
        {count === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">No unverified funded deals.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-amber-200">
                  <th className="text-left py-2 px-3 text-xs font-medium text-amber-600 uppercase tracking-wider">Deal #</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-amber-600 uppercase tracking-wider">Client</th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-amber-600 uppercase tracking-wider">Amount</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-amber-600 uppercase tracking-wider">Funded At</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-amber-600 uppercase tracking-wider">Evidence</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((deal) => (
                  <tr
                    key={deal.deal_id}
                    className="border-b border-amber-100 hover:bg-amber-50/50 cursor-pointer"
                    data-testid={`diag-unverified-deal-${deal.deal_number}`}
                    onClick={() => navigate(`/clients/${deal.client_id || deal.deal_id}`)}
                  >
                    <td className="py-2 px-3 font-medium text-blue-600 hover:underline">{deal.deal_number}</td>
                    <td className="py-2 px-3 text-blue-600 hover:underline font-medium">{deal.client_name || 'Unknown'}</td>
                    <td className="py-2 px-3 text-right font-medium text-amber-700">{formatCurrency(deal.amount)}</td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{deal.funded_at ? deal.funded_at.substring(0, 10) : '—'}</td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">
                        {(deal.evidence || []).map((e, i) => (
                          <Badge key={`${e}-${i}`} variant="secondary" className="bg-amber-100 text-amber-700 text-[10px]">
                            {e.split('=')[0]}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Diagnostic links / deep-dive grid ───
const DiagnosticLink = ({ to, icon: Icon, title, desc, testId }) => (
  <Link
    to={to}
    className="block group rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-400 hover:shadow-sm transition-all"
    data-testid={testId}
  >
    <div className="flex items-start gap-3">
      <div className="p-2 rounded-md bg-slate-100 group-hover:bg-slate-200 transition-colors">
        <Icon className="h-5 w-5 text-slate-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-700 transition-colors" />
        </div>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</p>
      </div>
    </div>
  </Link>
);

export default function AdminDiagnosticsPage() {
  useDocumentTitle('Admin Diagnostics');
  const { user } = useAuth();
  const role = user?.role;
  const isInternal = isInternalRole(role);
  const isAdmin = isAdminRole(role);

  // External users should never land here
  if (!isInternal) {
    return (
      <div className="px-4 md:px-8 py-8" data-testid="diag-access-denied">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-slate-500" />
              Restricted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">Admin diagnostics are not available to your role.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-4 space-y-4 max-w-7xl mx-auto" data-testid="admin-diagnostics-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Wrench className="h-6 w-6 text-slate-700" />
            <h1 className="text-2xl font-bold text-slate-900">Admin Diagnostics</h1>
            <Badge variant="secondary" className="bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider">
              v3.15-1036
            </Badge>
          </div>
          <p className="text-sm text-slate-600 max-w-3xl leading-relaxed">
            Integrity, audit, and migration tooling. <strong>Nothing on this page is operational.</strong>{' '}
            The deal lifecycle (<code className="text-[11px] bg-slate-100 px-1 rounded">all_parties_settled == true</code>) is
            the single source of truth for funding, payroll, settlement, and dashboards. Items here exist only to surface
            drift from that SSOT and to help admins clean up legacy data.
          </p>
        </div>
      </div>

      {/* SSOT rule banner */}
      <div className="rounded-lg border border-slate-300 bg-slate-50 p-4" data-testid="diag-ssot-rule">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Operational Funding Rule</p>
        <p className="text-sm text-slate-800 font-mono">
          A deal is funded <strong>if and only if</strong> <span className="text-emerald-700">all_parties_settled == true</span>
        </p>
        <p className="text-[11px] text-slate-600 mt-1">
          Legacy markers (vendor_payment_received_date, payment_confirmed, deal_stage=funded, funding_status=funded,
          ptc_paid_date, partner_paid_date, ptc_payment_received_date, settlement_status, invoice_status) are <strong>never</strong>{' '}
          used operationally — they appear here only for diagnostic purposes.
        </p>
      </div>

      {/* Live integrity chips — pull from existing dashboard widgets */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4 text-rose-600" />
            Live Integrity Signals
          </CardTitle>
          <p className="text-[11px] text-slate-500 mt-0.5">
            If any chip is hidden, the corresponding integrity count is zero — nothing needs your attention.
          </p>
        </CardHeader>
        <CardContent className="space-y-2 pt-2">
          <ReconcileAllChip />
          <SSOTIntegrityChip />
          <BookingSlugIssuesWarning />
          {/* Visible "all clear" hint when none of the above render */}
          <p className="text-[11px] text-emerald-700 font-medium" data-testid="diag-live-chips-hint">
            ✓ Any visible chips above are integrity drift; absence = all clear.
          </p>
        </CardContent>
      </Card>

      {/* Recent Email Sends (admin-only communication-integrity monitor) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4 text-slate-700" />
            Communication Integrity
          </CardTitle>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Last outbound emails with ownership-explanation badges. Confirms CC/recipient scope is honoring deal ownership.
          </p>
        </CardHeader>
        <CardContent className="pt-2">
          <RecentEmailSendsSection />
          {/* RecentEmailSendsSection returns null when empty — give an empty-state hint */}
          <p className="text-[11px] text-slate-500 font-medium" data-testid="diag-email-hint">
            If nothing renders above, no outbound emails have been sent in the recent window.
          </p>
        </CardContent>
      </Card>

      {/* Unverified Funded Deals */}
      <UnverifiedFundedDealsSection />

      {/* Deep-dive diagnostic surfaces */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-3">Deep-Dive Surfaces</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <DiagnosticLink
            to="/admin/funded-mismatches"
            icon={AlertTriangle}
            title="Funded Mismatches"
            desc="Deals presenting as funded via legacy stage/status but lacking SSOT confirmation. Review-only."
            testId="diag-link-funded-mismatches"
          />
          <DiagnosticLink
            to="/admin/reconcile"
            icon={Wrench}
            title="Reconcile All"
            desc="Combined report: Funded Without Payment + Funded But Active. Bulk reconciliation tooling."
            testId="diag-link-reconcile"
          />
          <DiagnosticLink
            to="/funded-this-month"
            icon={Activity}
            title="Funded This Month"
            desc="Drill-down on SSOT-funded deals in the current month with all underlying date fields exposed."
            testId="diag-link-funded-this-month"
          />
          <DiagnosticLink
            to="/funded-but-active"
            icon={AlertTriangle}
            title="Funded But Active"
            desc="Deals that are SSOT-funded but still have is_active=true. Cleanup tool."
            testId="diag-link-funded-but-active"
          />
          {isAdmin && (
            <DiagnosticLink
              to="/data-integrity"
              icon={Database}
              title="Data Integrity"
              desc="Schema-level integrity audits, orphan record scans, field-level mismatch reports."
              testId="diag-link-data-integrity"
            />
          )}
          <DiagnosticLink
            to="/settlement"
            icon={Shield}
            title="Settlement Report"
            desc="Per-deal settlement state. Funded gate is SSOT-only; payment legs (PTC/Partner) shown for already-funded deals."
            testId="diag-link-settlement"
          />
        </div>
      </div>

      {/* Footnote */}
      <div className="text-[11px] text-slate-500 leading-relaxed pt-2 border-t border-slate-200" data-testid="diag-footnote">
        <p>
          <strong>Reminder.</strong> Per-deal warnings (commission mismatch, PTC payment missing, settlement incomplete) appear
          INSIDE each deal. The global scanner pages on this page are intentionally separated from the operational dashboard.
          See <code className="text-[11px] bg-slate-100 px-1 rounded">/app/memory/FUNDING_SSOT_AUDIT_v3_15_1036.md</code> for
          the full migration manifest.
        </p>
      </div>
    </div>
  );
}
